import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import PrintIcon from "@mui/icons-material/Print";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";

import "react-quill/dist/quill.bubble.css";
import "./PatientCaseStudyManager.css";

import AddCaseStudy from "./AddCaseStudy";
import { doctorActions } from "../../core/actions";
import { helperServices } from "../../shared/helper";
import { PROCEDURE_CONSTANTS_DETAILS } from "../../shared/constants";
import {
  generatePatientCaseStudyPdf,
  openPatientCaseStudyPdfTab,
  renderPatientCaseStudyPdfTab,
} from "../../vm/VedamaayuPatientCaseStudyPrint";
import {
  isCaseStudyDateClass,
  isCaseStudyNotesClass,
  parseCaseStudyData,
} from "../../shared/helper/caseStudyUtils";

const { PROCEDURE_GROUPNAME_PATIENT_CASE_STUDY } =
  PROCEDURE_CONSTANTS_DETAILS;

const getDoctorDisplayName = (doctor) => {
  const fullName = helperServices.getFullName(
    doctor?.first_name || "",
    null,
    doctor?.last_name || "",
  );

  return (
    `${doctor?.displaytitle || ""} ${fullName}` +
    `${
      doctor?.department
        ? `, Dep of ${doctor.department}`
        : ""
    }` +
    `${
      doctor?.qualification
        ? ` (${doctor.qualification})`
        : ""
    }`
  ).trim();
};

// Formats the selected Case Study date without changing its stored value.
const formatCaseStudyDate = (value) => {
  if (!value) return "Date not available";

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "Date not available";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Formats the stored creation timestamp as a 12-hour time.
const formatCaseStudyCreatedTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const CaseStudyHeaderField = ({
  label,
  value,
  secondaryValue = "",
  className = "",
}) => (
  <div className={`case-study-card__header-field ${className}`}>
    {label && (
      <span className="case-study-card__label">
        {label}:
      </span>
    )}

    <span className="case-study-card__value">
      <span>{value || "Not available"}</span>
      {secondaryValue && (
        <span className="case-study-card__secondary-value">
          {secondaryValue}
        </span>
      )}
    </span>
  </div>
);

function PatientCaseStudyManager(props) {
  const dispatch = useDispatch();
  const isReadOnly = Boolean(props.readOnly);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [doctorNamesById, setDoctorNamesById] = useState({});

  useEffect(() => {
    dispatch(
      doctorActions.getDoctorList({}, (response) => {
        const namesById = (Array.isArray(response) ? response : [])
          .filter(
            (doctor) =>
              doctor?.type?.toLowerCase() === "doctor",
          )
          .reduce((result, doctor) => {
            result[String(doctor.personId)] =
              getDoctorDisplayName(doctor);
            return result;
          }, {});

        setDoctorNamesById(namesById);
      }),
    );
  }, [dispatch]);

  /*
   * Rebuild each Case Study from its paired Date and Notes rows.
   * Deleted records are excluded.
   */
  const caseStudies = useMemo(() => {
    const groupedStudies = new Map();

    (props.procedures || [])
      .filter(
        (procedure) =>
          procedure.groupname === PROCEDURE_GROUPNAME_PATIENT_CASE_STUDY &&
          !procedure.markForDel,
      )
      .forEach((procedure) => {
        const key =
          procedure.subgroup ||
          `legacy-${procedure.id}`;

        if (!groupedStudies.has(key)) {
          groupedStudies.set(key, {
            subgroup: key,
            dateRow: null,
            notesRow: null,
            legacyRow: null,
          });
        }

        const study = groupedStudies.get(key);

        if (isCaseStudyDateClass(procedure.claSS)) {
          study.dateRow = procedure;
        }

        if (isCaseStudyNotesClass(procedure.claSS)) {
          study.notesRow = procedure;
        } else if (!isCaseStudyDateClass(procedure.claSS)) {
          study.legacyRow = procedure;
        }
      });

    return Array.from(groupedStudies.values())
      .map((study) => {
        const sourceRow =
          study.notesRow || study.legacyRow || study.dateRow;
        const parsedStudy = parseCaseStudyData(
          sourceRow?.notes,
          sourceRow?.longdesc,
        );
        const notesRowLooksLegacy = String(
          study.notesRow?.notes || "",
        )
          .trim()
          .startsWith("{");
        const doctorId =
          study.notesRow?.doctorId ||
          study.dateRow?.doctorId ||
          study.legacyRow?.doctorId;

        return {
          ...study,
          patientId:
            study.notesRow?.patientId ||
            study.dateRow?.patientId ||
            study.legacyRow?.patientId,
          visitId:
            study.notesRow?.visitId ||
            study.dateRow?.visitId ||
            study.legacyRow?.visitId,
          date: study.dateRow?.notes || parsedStudy.date || "",
          createdAt:
            study.dateRow?.cdt ||
            study.notesRow?.cdt ||
            study.legacyRow?.cdt ||
            "",
          notes: notesRowLooksLegacy
            ? parsedStudy.notes || ""
            : study.notesRow?.notes ||
              parsedStudy.notes ||
              study.legacyRow?.longdesc ||
              "",
          doctorId,
          doctorName:
            doctorNamesById[String(doctorId)] ||
            parsedStudy.updatedBy?.name ||
            study.notesRow?.preparedBy ||
            study.dateRow?.preparedBy ||
            study.legacyRow?.preparedBy ||
            "Not available",
        };
      })
      .sort(
        (first, second) =>
          new Date(second.date) -
          new Date(first.date),
      );
  }, [doctorNamesById, props.procedures]);

  /*
   * Called when AddCaseStudy closes or finishes saving.
   */
  const handleFormResult = (result) => {
    if (!result) {
      setEditingId(null);
      setAddingNew(false);
      return;
    }

    if (result.success) {
      setEditingId(null);
      setAddingNew(false);
    }

    if (props.onRefresh) {
      props.onRefresh(result);
    }
  };

  /*
   * Display Quill Delta for new records and sanitized
   * HTML/plain text for older records.
   */
  const renderContent = (content) => {
    if (Array.isArray(content?.ops)) {
      return (
        <ReactQuill
          theme="bubble"
          value={content}
          readOnly
          modules={{ toolbar: false }}
        />
      );
    }

    return (
      <div
        className="case-study-card__legacy-content"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content || ""),
        }}
      />
    );
  };

  const handlePrint = async () => {
    // Open first so the browser does not block the generated PDF tab.
    let printWindow;

    try {
      printWindow = openPatientCaseStudyPdfTab();
      const docDefinition = await generatePatientCaseStudyPdf({
        patientInfo: props.patientInfo,
        patientData: props.patientData,
        caseStudies,
      });

      renderPatientCaseStudyPdfTab(printWindow, docDefinition);
    } catch (error) {
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      console.error(
        "Unable to print Patient Case Studies:",
        error,
      );

      props.onRefresh?.({
        error: "Unable to print Patient Case Studies.",
      });
    }
  };

  if (addingNew) {
    return (
      <AddCaseStudy
        key="new-case-study"
        patientData={props.patientData}
        attendingDoctor={props.attendingDoctor}
        mode="new"
        onModalClose={handleFormResult}
      />
    );
  }

  return (
    <div className="patient-case-study-manager">
      <div className="patient-case-study-manager__header">
        <div>
          <h3 className="patient-case-study-manager__title">
            Patient Case Studies
          </h3>
        </div>

        <div className="patient-case-study-manager__actions">
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={
              caseStudies.length === 0 ||
              addingNew ||
              editingId !== null
            }
            sx={{ textTransform: "none" }}
          >
            Print
          </Button>

          {!isReadOnly && !addingNew && editingId === null && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddingNew(true)}
              sx={{ textTransform: "none" }}
            >
              Add New Case Study
            </Button>
          )}

          <IconButton
            aria-label="Close Patient Case Studies"
            onClick={props.onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {!addingNew && caseStudies.length === 0 && (
        <div className="patient-case-study-manager__empty">
          No Patient Case Studies have been added yet.
        </div>
      )}

      <div className="patient-case-study-list">
        {caseStudies.map((caseStudy) => {
          const isEditing =
            editingId === caseStudy.subgroup;

          return (
            <div
              className={`case-study-card ${
                isEditing ? "case-study-card--editing" : ""
              }`}
              key={caseStudy.subgroup}
            >
              {isEditing ? (
                <AddCaseStudy
                  key={`edit-${caseStudy.subgroup}`}
                  patientData={props.patientData}
                  attendingDoctor={props.attendingDoctor}
                  mode="edit"
                  caseStudy={caseStudy}
                  onModalClose={handleFormResult}
                />
              ) : (
                <>
                  <div className="case-study-card__header">
                    <CaseStudyHeaderField
                      value={caseStudy.doctorName}
                    />

                    <CaseStudyHeaderField
                      value={formatCaseStudyDate(caseStudy.date)}
                      secondaryValue={formatCaseStudyCreatedTime(
                        caseStudy.createdAt,
                      )}
                      className="case-study-card__date-field"
                    />
                  </div>

                  <div className="case-study-card__content">
                    <div className="case-study-card__notes">
                      {renderContent(caseStudy.notes)}
                    </div>
                  </div>

                  {!isReadOnly && (
                    <div className="case-study-card__footer">
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setAddingNew(false);
                          setEditingId(caseStudy.subgroup);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PatientCaseStudyManager;
